/** Typed calls for every /admin/v1 route the console uses (the backend's src/http/admin). */

import type { AdminClient } from "./client";

export type Role = "owner" | "editor" | "viewer";
export interface Staff { accountId: string; email: string; role: Role }

export const ENTITY_TYPES = ["title", "series", "liveEvent", "collection", "page", "person", "taxonomy", "stage"] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];
export type EntityStatus = "draft" | "ready" | "archived";

/** Where an item stands against the apps (the backend's cms/domain/publish-state.ts): the badge. */
export type PublishState = "draft" | "new" | "live" | "changed" | "leaving" | "archived" | "unknown";
export interface EntitySummary { type: EntityType; id: string; status: EntityStatus; revision: number; label: string; updatedAt: number; updatedBy: string | null; publishState?: PublishState }
export interface Entity extends EntitySummary { data: Record<string, unknown>; createdAt: number }

/** One thing the next publish changes in the apps. */
export interface PendingChange { type: EntityType; id: string; label: string; change: "add" | "update" | "remove" }
export interface ChangesView {
  live: { version: number; publishedAt: string; publishedBy: string | null } | null;
  /** False when the live version was published before items were recorded: changes are unknown until the next publish. */
  known: boolean;
  changes: PendingChange[];
}

/** `field`: the path inside the item ("playback.video.videoId"), so the editor can open that field. */
export interface Problem { level: "error" | "warning"; itemId?: string; path?: string; field?: string; message: string }
export interface PublishRecord { version: number; kind: "publish" | "rollback" | "refresh"; sourceVersion: number | null; publishedAt: number; publishedBy: string | null; generatedAt: string; bytes: number; summary: Record<string, unknown>; items: Record<string, number> | null }
export interface AuditEntry { id: string; at: number; actorEmail: string | null; action: string; targetType: string | null; targetId: string | null; detail: Record<string, unknown> | null }
export interface VoucherBatch { id: string; label: string; productId: string; count: number; createdAt: number; redeemed: number; revoked: boolean }
export interface Grant { id: string; accountId: string | null; email: string | null; productId: string; source: "voucher" | "guest_list" | "comp"; expiresAt: number | null; createdAt: number }

export const session = {
  current: (c: AdminClient) => c.call<{ staff: Staff }>("GET", "/admin/v1/session"),
  emailStart: (c: AdminClient, email: string) => c.call<{ sent: true }>("POST", "/admin/v1/session/email/start", { body: { email } }),
  emailComplete: (c: AdminClient, email: string, code: string) => c.call<{ staff: Staff }>("POST", "/admin/v1/session/email/complete", { body: { email, code } }),
  passkeyOptions: (c: AdminClient) => c.call<{ challengeId: string; options: unknown }>("POST", "/admin/v1/session/passkey/options"),
  passkeyComplete: (c: AdminClient, challengeId: string, response: unknown) => c.call<{ staff: Staff }>("POST", "/admin/v1/session/passkey/complete", { body: { challengeId, response } }),
  end: (c: AdminClient) => c.call<void>("DELETE", "/admin/v1/session"),
};

export const entities = {
  list: (c: AdminClient, q: { type?: EntityType; status?: EntityStatus; q?: string } = {}) => {
    const qs = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString();
    return c.call<{ entities: EntitySummary[] }>("GET", `/admin/v1/entities${qs ? `?${qs}` : ""}`);
  },
  /** With their documents: for views that draw from the data (the schedule grid, availability windows). Every type when none is given. */
  listFull: (c: AdminClient, type?: EntityType) => c.call<{ entities: Entity[] }>("GET", `/admin/v1/entities?${type ? `type=${type}&` : ""}full=1&limit=500`),
  get: (c: AdminClient, type: EntityType, id: string) => c.call<{ entity: Entity }>("GET", `/admin/v1/entities/${type}/${encodeURIComponent(id)}`),
  create: (c: AdminClient, type: EntityType, id: string, data: unknown, status: EntityStatus) => c.call<{ entity: Entity }>("POST", `/admin/v1/entities/${type}`, { body: { id, data, status } }),
  save: (c: AdminClient, type: EntityType, id: string, data: unknown, status: EntityStatus, revision: number) =>
    c.call<{ entity: Entity }>("PUT", `/admin/v1/entities/${type}/${encodeURIComponent(id)}`, { body: { data, status }, revision }),
  remove: (c: AdminClient, type: EntityType, id: string, revision: number) => c.call<void>("DELETE", `/admin/v1/entities/${type}/${encodeURIComponent(id)}`, { revision }),
  revisions: (c: AdminClient, type: EntityType, id: string) => c.call<{ revisions: Array<{ revision: number; status: EntityStatus; savedAt: number; savedBy: string | null }> }>("GET", `/admin/v1/entities/${type}/${encodeURIComponent(id)}/revisions`),
  restore: (c: AdminClient, type: EntityType, id: string, from: number, revision: number) =>
    c.call<{ entity: Entity }>("POST", `/admin/v1/entities/${type}/${encodeURIComponent(id)}/restore`, { body: { revision: from }, revision }),
};

export const publishing = {
  settings: (c: AdminClient) => c.call<{ brandId: string; defaultLocale: string }>("GET", "/admin/v1/settings"),
  saveSettings: (c: AdminClient, defaultLocale: string) => c.call<{ defaultLocale: string }>("PUT", "/admin/v1/settings", { body: { defaultLocale } }),
  changes: (c: AdminClient) => c.call<ChangesView>("GET", "/admin/v1/changes"),
  validate: (c: AdminClient, include: "published" | "preview" = "published") => c.call<{ ok: boolean; problems: Problem[] }>("GET", `/admin/v1/validate?include=${include}`),
  publish: (c: AdminClient) => c.call<{ publish: PublishRecord; warnings: Problem[] }>("POST", "/admin/v1/publish"),
  history: (c: AdminClient) => c.call<{ publishes: PublishRecord[] }>("GET", "/admin/v1/publishes"),
  rollback: (c: AdminClient, version: number) => c.call<{ publish: PublishRecord }>("POST", `/admin/v1/publishes/${version}/rollback`),
  importFeed: (c: AdminClient, feed: unknown) => c.call<{ kind: "imported"; created: number; updated: number; problems: Problem[] }>("POST", "/admin/v1/import", { body: { feed } }),
  previewToken: (c: AdminClient) => c.call<{ token: string; expiresAt: number }>("POST", "/admin/v1/preview-token"),
};

/** What the brand sells (its store products), with display prices. */
export interface ProductOffer { productId: string; kind: "subscription" | "purchase" | "rental"; rentalHours?: number; price?: string }
export const products = {
  list: (c: AdminClient) => c.call<{ products: ProductOffer[]; demoPurchases: boolean }>("GET", "/admin/v1/products"),
};

export const people = {
  staff: (c: AdminClient) => c.call<{ staff: Array<Staff & { createdAt: number }> }>("GET", "/admin/v1/staff"),
  setStaff: (c: AdminClient, email: string, role: Role) => c.call<{ member: Staff }>("PUT", "/admin/v1/staff", { body: { email, role } }),
  removeStaff: (c: AdminClient, accountId: string) => c.call<void>("DELETE", `/admin/v1/staff/${encodeURIComponent(accountId)}`),
  audit: (c: AdminClient, before?: number) => c.call<{ entries: AuditEntry[] }>("GET", `/admin/v1/audit${before ? `?before=${before}` : ""}`),
};

export const grants = {
  batches: (c: AdminClient) => c.call<{ batches: VoucherBatch[] }>("GET", "/admin/v1/voucher-batches"),
  createBatch: (c: AdminClient, input: { label: string; productId: string; count: number; maxRedemptions?: number; grantDays?: number | null; expiresAt?: string | null }) =>
    c.call<{ batchId: string; codes: string[] }>("POST", "/admin/v1/voucher-batches", { body: input }),
  revokeBatch: (c: AdminClient, id: string) => c.call<{ revoked: number }>("POST", `/admin/v1/voucher-batches/${encodeURIComponent(id)}/revoke`),
  list: (c: AdminClient, q: { productId?: string; source?: string; email?: string } = {}) => {
    const qs = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString();
    return c.call<{ grants: Grant[] }>("GET", `/admin/v1/grants${qs ? `?${qs}` : ""}`);
  },
  addGuests: (c: AdminClient, productId: string, emails: string[], expiresAt?: string | null) => c.call<{ added: number; duplicates: number; invalid: string[] }>("POST", "/admin/v1/guests", { body: { productId, emails, expiresAt } }),
  comp: (c: AdminClient, email: string, productId: string, days?: number | null) => c.call<{ grantId: string }>("POST", "/admin/v1/comps", { body: { email, productId, days } }),
  revoke: (c: AdminClient, id: string) => c.call<{ ok: true }>("POST", `/admin/v1/grants/${encodeURIComponent(id)}/revoke`),
};

export interface MediaAsset {
  id: string;
  kind: "video" | "image";
  status: "uploading" | "ready" | "failed" | "purged";
  title: string;
  storedBytes: number;
  reservedBytes: number;
  details: Record<string, unknown> | null;
  error: string | null;
  createdAt: number;
  updatedAt: number;
  /** What a feed holds for it (ready assets): a playback block for a video, an Image for artwork. */
  feed: Record<string, unknown> | null;
  /** A ready video's master playlist, for the editor's timeline. */
  manifestUrl: string | null;
  /** An unfinished video: still arriving, waiting for the studio's encoder, or encoding. */
  stage?: "uploading" | "waiting_for_encoder" | "encoding";
}

export interface Budget { budgetBytes: number; storedBytes: number; reservedBytes: number; freeBytes: number }
export interface ImageUploadInput { title: string; role: string; sha256: string; sizes: Array<{ width: number; height: number; bytes: number }>; color: string; blurhash: string; focal: { x: number; y: number } }

export const media = {
  list: (c: AdminClient, q: { kind?: "video" | "image"; status?: string } = {}) => {
    const qs = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString();
    return c.call<{ assets: MediaAsset[] }>("GET", `/admin/v1/media/assets${qs ? `?${qs}` : ""}`);
  },
  budget: (c: AdminClient) => c.call<Budget>("GET", "/admin/v1/media/budget"),
  startVideo: (c: AdminClient, input: { title: string; bytes: number; contentType: string; durationSeconds?: number; ladder: string }) =>
    c.call<{ asset: MediaAsset; uploadId: string; partBytes: number; parts: Array<{ partNumber: number; url: string }> }>("POST", "/admin/v1/media/videos", { body: input }),
  completeVideo: (c: AdminClient, id: string, uploadId: string, ladder: string, parts: Array<{ partNumber: number; etag: string }>) =>
    c.call<{ queued: true; jobId: string }>("POST", `/admin/v1/media/videos/${encodeURIComponent(id)}/complete`, { body: { uploadId, ladder, parts } }),
  abortVideo: (c: AdminClient, id: string, uploadId: string) => c.call<{ ok: true }>("POST", `/admin/v1/media/videos/${encodeURIComponent(id)}/abort`, { body: { uploadId } }),
  startImage: (c: AdminClient, input: ImageUploadInput) =>
    c.call<{ asset: MediaAsset; existing?: true; uploads?: Array<{ width: number; url: string; headers: Record<string, string> }> }>("POST", "/admin/v1/media/images", { body: input }),
  finishImage: (c: AdminClient, id: string, input: ImageUploadInput) => c.call<{ asset: MediaAsset }>("POST", `/admin/v1/media/images/${encodeURIComponent(id)}/finish`, { body: input }),
  remove: (c: AdminClient, id: string) => c.call<void>("DELETE", `/admin/v1/media/assets/${encodeURIComponent(id)}`),
};

/** The brand manifest (the backend's src/brand): editable parts saved here, published with the feed. */
export interface BrandManifest {
  id: string;
  name: string;
  tagline?: string;
  locales: { default: string; supported: string[] };
  colors: { accent: string; background?: string; surface?: string };
  fonts: { display?: { family: string; source: string }; body?: { family: string; source: string } };
  backend: { baseUrl: string; activateUrl: string; accountPortalUrl: string };
  stores: { apple?: { bundleId: string }; google?: { packageName: string }; amazon?: { packageName: string } };
  features: { profiles: boolean; kidsMode: boolean; downloads: boolean; live: boolean; cast: boolean; search: boolean; give?: { url: string } };
  menu: Array<{ pageId: string; label: Record<string, string> }>;
  legal: { privacyUrl: string; termsUrl: string; supportEmail: string };
}
export interface BrandView {
  revision: number;
  manifest: BrandManifest;
  /** Editable keys that differ from the manifest the apps were built with. */
  changed: string[];
  updatedAt: number | null;
  updatedBy: string | null;
  publishedUrl: string | null;
}

export const brand = {
  get: (c: AdminClient) => c.call<BrandView>("GET", "/admin/v1/brand"),
  save: (c: AdminClient, manifest: Partial<BrandManifest>, revision: number) => c.call<BrandView>("PUT", "/admin/v1/brand", { body: { manifest }, revision }),
};
